// Sign-in identifiers (emails, phone numbers) — normalization and hashing shared by every layer.
// A user has one primary email (the engine's `user.email`, OIDC `email`) plus any number of additional
// emails / phone numbers kept by the platform (platform_user_identifier). See docs/identifiers.md.
import { emailHash, normalizeEmail, sha256Hex } from "./crypto";

export type IdentifierType = "email" | "phone" | "user_code" | "username";
export const IDENTIFIER_TYPES = [
  "email",
  "phone",
  "user_code",
  "username",
] as const satisfies readonly IdentifierType[];

/** Name-like sign-in identifiers (no verification, at most one of each per user, one shared namespace). */
export const NAME_IDENTIFIER_TYPES = ["user_code", "username"] as const satisfies readonly IdentifierType[];
export const isNameIdentifier = (t: IdentifierType): t is "user_code" | "username" =>
  t === "user_code" || t === "username";

/** Built-in superuser's user code, seeded like Argo CD's `admin` (see docs/root-account.md). */
export const ROOT_USER_CODE = "root";
/** Internal address of the root account (`.invalid` is reserved: nothing is ever delivered). */
export const ROOT_EMAIL = "root@root.invalid";

const NAME_RE = /^[a-z0-9][a-z0-9._-]{2,31}$/;

/**
 * User code ("รหัสผู้ใช้งาน", identifier type `user_code`) and username ("ชื่อผู้ใช้", type `username`) share one format
 * (ADR 022): chosen at sign-up or set by an admin, unique across both types, case-insensitive (stored lower-case).
 * 3–32 characters: letters or digits first, then letters, digits, `.`, `_`, `-`. Numeric values are fine (`100234`),
 * but 9+ digits read as a phone number and are refused so a name never shadows a phone number. Sign-in resolves a
 * phone-looking input as a user code / username when no account has that phone number.
 */
export function normalizeUserCode(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (!NAME_RE.test(v)) return null;
  const phoneLike = /^[0-9 +-]+$/.test(v) && v.replace(/\D/g, "").length >= 9;
  return phoneLike ? null : v;
}

/** Username ("ชื่อผู้ใช้") — same format as a user code (ADR 022). */
export const normalizeUsername = normalizeUserCode;

/** Per user and per type (the primary email included). */
export const MAX_IDENTIFIERS_PER_TYPE = 10;

/** Default country calling code for national phone numbers (Thailand). Override with DEFAULT_PHONE_COUNTRY_CODE. */
export const DEFAULT_PHONE_COUNTRY_CODE = "66";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  const v = normalizeEmail(value);
  return v.length <= 254 && EMAIL_RE.test(v);
}

/**
 * Normalize a phone number to E.164 (`+<country><number>`) without external libraries.
 * Accepts `+CC…`, `00CC…`, national numbers with a trunk `0` (→ default country code) and bare
 * international digits; spaces, dashes, dots and parentheses are ignored. Returns null when the input is
 * not a plausible phone number (8–15 digits, E.164 limits).
 */
export function normalizePhone(
  input: string,
  defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
): string | null {
  const raw = input.trim();
  if (!raw || /[^\d+\s\-().]/.test(raw)) return null;
  let digits = raw.replace(/[\s\-().]/g, "");
  if (digits.startsWith("+")) digits = digits.slice(1);
  else if (digits.startsWith("00")) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = defaultCountryCode + digits.slice(1);
  else if (digits.length <= 10) digits = defaultCountryCode + digits;
  if (!/^[1-9]\d{7,14}$/.test(digits)) return null;
  return `+${digits}`;
}

/** Normalize a value of a known type; null when invalid. */
export function normalizeIdentifier(
  type: IdentifierType,
  value: string,
  defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
): string | null {
  if (type === "email") return isValidEmail(value) ? normalizeEmail(value) : null;
  if (isNameIdentifier(type)) return normalizeUserCode(value);
  return normalizePhone(value, defaultCountryCode);
}

/**
 * Classify free text typed into a sign-in field. A name-like value is reported as `user_code`; sign-in looks it up
 * as a user code and then as a username (one namespace, so at most one matches).
 */
export function detectIdentifier(
  input: string,
  defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
): { type: IdentifierType; value: string } | null {
  const v = input.trim();
  if (v.includes("@")) return isValidEmail(v) ? { type: "email", value: normalizeEmail(v) } : null;
  const phone = normalizePhone(v, defaultCountryCode);
  if (phone) return { type: "phone", value: phone };
  const name = normalizeUserCode(v);
  return name ? { type: "user_code", value: name } : null;
}

/** Hash used in audit logs instead of the raw value (emails hash exactly like `emailHash`). */
export function identifierHash(type: IdentifierType, value: string): Promise<string> {
  return type === "email" ? emailHash(value) : sha256Hex(value);
}

/** Masked form for user-facing messages ("code sent to …"): `so***@example.com`, `+66•••••5678`. */
export function maskIdentifier(type: IdentifierType, value: string): string {
  if (isNameIdentifier(type)) return value;
  if (type === "email") {
    const [local = "", domain = ""] = value.split("@");
    return `${local.slice(0, 2)}${"*".repeat(Math.max(1, Math.min(local.length - 2, 5)))}@${domain}`;
  }
  const cc = value.startsWith("+66") ? "+66" : value.slice(0, 3);
  return `${cc}${"•".repeat(Math.max(1, value.length - cc.length - 4))}${value.slice(-4)}`;
}

/**
 * Readable form for display (values are stored and compared in E.164): Thai numbers as
 * `+66 81 234 5678` / `+66 2 123 4567`, others as `+CC rest`. Emails are returned unchanged.
 */
export function formatIdentifier(type: IdentifierType, value: string): string {
  if (type === "email" || isNameIdentifier(type)) return value;
  const m66 = /^\+66(\d{9})$/.exec(value);
  if (m66) return `+66 ${m66[1]!.replace(/^(\d{2})(\d{3})(\d{4})$/, "$1 $2 $3")}`;
  const land66 = /^\+66(\d)(\d{3})(\d{4})$/.exec(value);
  if (land66) return `+66 ${land66[1]} ${land66[2]} ${land66[3]}`;
  const other = /^\+(1|7|\d{2})(\d+)$/.exec(value);
  return other ? `+${other[1]} ${other[2]}` : value;
}

const PASSWORD_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789"; // no 0/O, 1/l/I

/** Random password for generated credentials (e.g. the initial root password), uniform over the alphabet. */
export function generatePassword(length = 24): string {
  const out: string[] = [];
  const buf = new Uint8Array(length * 2);
  while (out.length < length) {
    globalThis.crypto.getRandomValues(buf);
    for (const b of buf) {
      // 256 − 256 % 56 = 224: reject the tail so every character is equally likely.
      if (b < 224 && out.length < length) out.push(PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]!);
    }
  }
  return out.join("");
}

/** Six-digit numeric one-time code (WebCrypto, uniform). */
export function generateOtp(): string {
  const buf = new Uint32Array(1);
  for (;;) {
    globalThis.crypto.getRandomValues(buf);
    // Reject the tail of the 2^32 range so every code is equally likely.
    if (buf[0]! < 4_294_000_000) return String(buf[0]! % 1_000_000).padStart(6, "0");
  }
}
